/**
 * Cron expressions, validated and described.
 *
 * The validator is a copy of `maxlayer-api/src/lib/cron.ts`, kept in step by
 * hand. Copied rather than shared because the four repositories deploy
 * independently and none depends on another at build time.
 *
 * The two exist for different jobs and the server's is the authority: this one
 * turns a half-typed expression into inline feedback and a plain-English
 * preview while somebody is still in the field, and the API validates again on
 * every write regardless of what the form thought. A divergence between them
 * shows up as a form that accepts something the API refuses, which the API's
 * own suite pins -- it is a worse form, never a way past the check.
 *
 * **Below the validator this file diverges deliberately**, and the extra parts
 * are display-only: `PRESET_CRONS` for the form's one-click choices, and
 * `nextRun` for "runs next" on a row. Neither belongs on the Worker -- it never
 * needs to know when a job fires, only whether the expression is one it will
 * store -- and shipping them there would be bundle weight on the control plane
 * for nothing.
 *
 * It **rejects rather than clamps**. A cron the platform helpfully corrects is
 * one the customer did not write, and a job that runs at a time nobody asked
 * for is worse than a form that refuses.
 */

import i18next from "i18next";
import { intlLocaleFor } from "../i18n/locale";

/**
 * The shortest interval a schedule may run at, in minutes.
 *
 * Each schedule is one cron entry on a host shared by many tenants, and the
 * cost of a once-a-minute job across a few hundred of them is borne by that
 * machine's scheduler rather than by the $1. Five minutes is deliberately
 * cautious for a first version: relaxing a floor later is not a breaking
 * change, and tightening one is.
 *
 * A product rule rather than a per-environment one, so it lives here as a
 * constant instead of in the environment.
 */
export const MIN_INTERVAL_MINUTES = 5;

/*
 * Read through i18next directly rather than through a hook, because this is
 * called from a validator and a list renderer as well as from a component —
 * the same reason `format.ts` does. Every string is in the `schedules`
 * namespace, next to the form that shows them.
 */
const schedulesT = () => i18next.getFixedT(null, "schedules");

export type CronFieldName =
  | "minute"
  | "hour"
  | "day of month"
  | "month"
  | "day of week"
  | "expression";

export type CronCheck =
  | { ok: true; description: string }
  | { ok: false; field: CronFieldName; reason: string };

/**
 * Every value one field matches, expanded.
 *
 * Returns null on anything malformed rather than throwing, because the caller
 * wants to name the offending field in a 400 rather than catch.
 *
 * Handles the four forms and comma lists of them: a star, a number, a range
 * `a-b`, and a step (a star or a range, then a slash and a divisor).
 * Deliberately not the extensions some daemons take -- names like `MON`,
 * macros like `@daily`, and the `?`, `L`, `W` and `#` day-of-week operators --
 * because the parser that has to agree with this one is Dokploy's scheduler,
 * and the intersection of what every cron implementation accepts is the safe
 * set to store.
 */
function expand(part: string, min: number, max: number): number[] | null {
  const values = new Set<number>();

  for (const piece of part.split(",")) {
    if (piece === "") return null;

    const slash = piece.indexOf("/");
    const range = slash === -1 ? piece : piece.slice(0, slash);
    const stepText = slash === -1 ? null : piece.slice(slash + 1);

    let step = 1;
    if (stepText !== null) {
      if (!/^\d+$/.test(stepText)) return null;
      step = Number(stepText);
      if (step < 1 || step > max) return null;
    }

    let from: number;
    let to: number;

    if (range === "*") {
      from = min;
      to = max;
    } else if (/^\d+$/.test(range)) {
      from = Number(range);
      // A bare number with a step means "from here to the end", which is what
      // every cron implementation does with `30/15` and what a reader expects.
      to = stepText === null ? from : max;
    } else {
      const bounds = /^(\d+)-(\d+)$/.exec(range);
      if (!bounds) return null;
      from = Number(bounds[1]);
      to = Number(bounds[2]);
    }

    if (from < min || to > max || from > to) return null;
    for (let value = from; value <= to; value += step) values.add(value);
  }

  return values.size > 0 ? [...values].sort((a, b) => a - b) : null;
}

/** The smallest distance between neighbours in a sorted list. */
function minAdjacentGap(values: number[]): number {
  let gap = Number.POSITIVE_INFINITY;
  let previous: number | undefined;
  for (const value of values) {
    if (previous !== undefined) gap = Math.min(gap, value - previous);
    previous = value;
  }
  return gap;
}

const first = (values: number[]): number => values[0] ?? 0;
const last = (values: number[]): number => values[values.length - 1] ?? 0;

/**
 * The shortest gap between two consecutive firings, in minutes.
 *
 * Only the minute and hour fields can bring a schedule below an hour, so the
 * coarser three are irrelevant to the floor: a job restricted to one day of the
 * month still fires on its minute pattern all through that day. Computing the
 * gap from the minute set within an hour, and folding in whether consecutive
 * hours are selected, covers every case the floor is meant to catch.
 */
function shortestGapMinutes(minutes: number[], hours: number[]): number {
  const DAY = 24 * 60;

  if (minutes.length === 1) {
    // One firing per selected hour, so the gap is however far apart the hours
    // are. Adjacent hours give 60, which clears any sane floor.
    if (hours.length === 1) return DAY;
    const between = minAdjacentGap(hours) * 60;
    // The wrap from the last selected hour back to the first, next day.
    const wrap = (24 - last(hours) + first(hours)) * 60;
    return Math.min(between, wrap);
  }

  let gap = Math.min(60, minAdjacentGap(minutes));

  // The wrap from the last minute of one hour into the first of the next is
  // only a real gap when that next hour also fires. Restricted to a single
  // hour, the distance to the next firing is a whole day.
  const hoursAreConsecutive = hours.length === 24 || minAdjacentGap(hours) === 1;
  if (hoursAreConsecutive) {
    gap = Math.min(gap, 60 - last(minutes) + first(minutes));
  }

  return gap;
}

const FIELD_KEYS = {
  minute: "cron.fields.minute",
  hour: "cron.fields.hour",
  "day of month": "cron.fields.dayOfMonth",
  month: "cron.fields.month",
  "day of week": "cron.fields.dayOfWeek",
  expression: "cron.fields.expression",
} as const satisfies Record<CronFieldName, string>;

function rejection(field: CronFieldName, text: string, min: number, max: number): CronCheck {
  const t = schedulesT();
  return {
    ok: false,
    field,
    reason: t("cron.invalidField", { text, field: t(FIELD_KEYS[field]), min, max }),
  };
}

/**
 * Whether an expression is one this platform will store, and what it means.
 *
 * The description is for the form and the list -- it is generated from the same
 * parse that validated the expression, so the two cannot disagree about what a
 * cron does.
 */
export function validateCron(expression: string): CronCheck {
  const t = schedulesT();
  const parts = expression.trim().split(/\s+/).filter((part) => part !== "");
  const [minuteText, hourText, domText, monthText, dowText] = parts;

  if (
    parts.length !== 5 ||
    minuteText === undefined ||
    hourText === undefined ||
    domText === undefined ||
    monthText === undefined ||
    dowText === undefined
  ) {
    return {
      ok: false,
      field: "expression",
      reason: t("cron.fiveFields", { count: parts.length }),
    };
  }

  // Expanded one at a time rather than in a loop, so each failure names its own
  // field without indexing back into a parallel array.
  const minutes = expand(minuteText, 0, 59);
  if (!minutes) return rejection("minute", minuteText, 0, 59);
  const hours = expand(hourText, 0, 23);
  if (!hours) return rejection("hour", hourText, 0, 23);
  const doms = expand(domText, 1, 31);
  if (!doms) return rejection("day of month", domText, 1, 31);
  const months = expand(monthText, 1, 12);
  if (!months) return rejection("month", monthText, 1, 12);
  const dows = expand(dowText, 0, 6);
  if (!dows) return rejection("day of week", dowText, 0, 6);

  const gap = shortestGapMinutes(minutes, hours);
  if (gap < MIN_INTERVAL_MINUTES) {
    return {
      ok: false,
      field: "expression",
      reason: t("cron.tooFrequent", { min: MIN_INTERVAL_MINUTES, count: gap }),
    };
  }

  return {
    ok: true,
    description: describeParsed(
      { minuteText, hourText, domText, monthText, dowText },
      { minutes, hours, doms, months, dows },
    ),
  };
}

/*
 * Named by `Intl` in the reader's language rather than from a list, for the
 * same reason `format.ts` dates through it: the list would be a second copy of
 * something the runtime already knows. 2024-01-07 is a Sunday, which is what
 * makes day 0 of a cron the day `Intl` names first.
 */
const nameLocale = () => intlLocaleFor(i18next.language);
const dayName = (day: number) =>
  new Intl.DateTimeFormat(nameLocale(), { weekday: "long", timeZone: "UTC" }).format(Date.UTC(2024, 0, 7 + day));
const monthName = (month: number) =>
  new Intl.DateTimeFormat(nameLocale(), { month: "long", timeZone: "UTC" }).format(Date.UTC(2024, month - 1, 1));
const pad = (value: number) => String(value).padStart(2, "0");

/**
 * Plain English for an expression already known to be valid.
 *
 * Covers the shapes people actually write and falls back to naming what it does
 * know for anything more elaborate. A confident wrong description would be
 * worse than none, so the fallbacks are honest rather than clever.
 */
function describeParsed(
  text: {
    minuteText: string;
    hourText: string;
    domText: string;
    monthText: string;
    dowText: string;
  },
  sets: { minutes: number[]; hours: number[]; doms: number[]; months: number[]; dows: number[] },
): string {
  const t = schedulesT();
  const { minutes, hours, doms, months, dows } = sets;

  const everyMonth = text.monthText === "*";
  const everyDom = text.domText === "*";
  const everyDow = text.dowText === "*";
  const everyDay = everyDom && everyMonth && everyDow;

  const stepMinutes = /^\*\/(\d+)$/.exec(text.minuteText);
  if (stepMinutes && text.hourText === "*" && everyDay) {
    return t("cron.describe.everyMinutes", { n: stepMinutes[1] });
  }

  const stepHours = /^\*\/(\d+)$/.exec(text.hourText);
  if (minutes.length === 1 && stepHours && everyDay) {
    return t("cron.describe.everyHours", { n: stepHours[1], minute: pad(first(minutes)) });
  }

  if (minutes.length === 1 && text.hourText === "*" && everyDay) {
    return t("cron.describe.everyHour", { minute: pad(first(minutes)) });
  }

  // A single time of day, which is overwhelmingly the common case, plus
  // whichever of the three coarse fields narrows it.
  if (minutes.length === 1 && hours.length === 1) {
    const at = `${pad(first(hours))}:${pad(first(minutes))}`;

    if (everyDay) return t("cron.describe.everyDay", { at });
    if (everyDom && everyMonth && dows.length <= 3) {
      return t("cron.describe.everyDays", { days: dows.map(dayName).join(", "), at });
    }
    if (everyDow && everyMonth && doms.length === 1) {
      return t("cron.describe.dayOfMonth", { day: first(doms), at });
    }
    if (everyDow && months.length === 1 && doms.length === 1) {
      return t("cron.describe.date", { month: monthName(first(months)), day: first(doms), at });
    }
    return t("cron.describe.restricted", { at });
  }

  const written = [text.minuteText, text.hourText, text.domText, text.monthText, text.dowText];
  return t("cron.describe.raw", { expression: written.join(" ") });
}

/**
 * The description alone, for display next to a stored expression.
 *
 * Assumes the expression has already been validated, which every stored one
 * has been. An invalid one is echoed back rather than throwing: this is called
 * while rendering a list, and one bad row must not blank the page.
 */
export function describeCron(expression: string): string {
  const check = validateCron(expression);
  return check.ok ? check.description : expression;
}

/**
 * Whether a string is an IANA zone this runtime can evaluate.
 *
 * Asked of the runtime rather than checked against a list, because the list
 * that matters is the one `Intl` actually has -- and because a hard-coded set
 * goes stale every time a country changes its rules.
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The four shapes people reach for, offered as one-click choices.
 *
 * Every one clears MIN_INTERVAL_MINUTES, and the words say what they do rather
 * than naming the cron -- somebody who knows cron can type it, and somebody who
 * does not should not have to learn it to run something nightly. The words are
 * `schedules.json` under `preset`; this holds the key and the expression.
 */
export const PRESET_CRONS = [
  { key: "hourly", cron: "0 * * * *" },
  { key: "daily", cron: "0 3 * * *" },
  { key: "weekly", cron: "0 3 * * 1" },
  { key: "monthly", cron: "0 3 1 * *" },
] as const;

/* ---------------------------------------------------------------- next run */

export interface CronSets {
  minutes: number[];
  hours: number[];
  doms: number[];
  months: number[];
  dows: number[];
}

/** The expanded sets of a valid expression, or null if it is not one. */
export function parseCron(expression: string): CronSets | null {
  const parts = expression.trim().split(/\s+/).filter((part) => part !== "");
  const [minuteText, hourText, domText, monthText, dowText] = parts;
  if (
    parts.length !== 5 ||
    minuteText === undefined ||
    hourText === undefined ||
    domText === undefined ||
    monthText === undefined ||
    dowText === undefined
  ) {
    return null;
  }

  const minutes = expand(minuteText, 0, 59);
  const hours = expand(hourText, 0, 23);
  const doms = expand(domText, 1, 31);
  const months = expand(monthText, 1, 12);
  const dows = expand(dowText, 0, 6);
  if (!minutes || !hours || !doms || !months || !dows) return null;

  return { minutes, hours, doms, months, dows };
}

/** An instant's wall-clock reading in a given zone. */
function partsIn(instant: Date, timeZone: string) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
  });

  const found: Record<string, string> = {};
  for (const part of formatter.formatToParts(instant)) found[part.type] = part.value;

  const WEEKDAYS: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    year: Number(found.year),
    month: Number(found.month),
    day: Number(found.day),
    // `24` at midnight is an `hourCycle` quirk rather than a real hour.
    hour: Number(found.hour) % 24,
    minute: Number(found.minute),
    second: Number(found.second),
    weekday: WEEKDAYS[found.weekday ?? "Sun"] ?? 0,
  };
}

/** How far the zone is from UTC at that instant, in milliseconds. */
function offsetAt(instant: Date, timeZone: string): number {
  const p = partsIn(instant, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - instant.getTime();
}

/**
 * The instant at which a wall-clock time occurs in a zone.
 *
 * Two passes, because the offset depends on the instant and the instant depends
 * on the offset. Guess by treating the wall time as UTC, measure the offset
 * there, correct, then re-measure once in case the correction crossed a
 * daylight-saving boundary.
 *
 * The one case this cannot get right is a wall time inside a spring-forward gap
 * -- 02:30 on a morning where 02:00 becomes 03:00 -- because that time does not
 * occur at all. It resolves to the instant just after the jump, which is when
 * cron implementations run such a job anyway.
 */
function instantFrom(
  wall: { year: number; month: number; day: number; hour: number; minute: number },
  timeZone: string,
): Date {
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, 0);
  const first = offsetAt(new Date(asUtc), timeZone);
  const corrected = asUtc - first;
  const second = offsetAt(new Date(corrected), timeZone);
  return new Date(second === first ? corrected : asUtc - second);
}

/**
 * When this expression next fires, evaluated in its own timezone.
 *
 * Walked forward a day at a time rather than solved, for the reason
 * `nextRunAt` in BackupPanel gives about its own narrower version: a
 * closed-form answer would be subtly wrong about month ends, leap years and the
 * turn of a week, in a way nobody notices until the one day a year it matters.
 *
 * Day-of-month and day-of-week are **OR** when both are restricted, which is
 * what every cron implementation does and what surprises people who expect AND:
 * `0 3 1 * 1` fires on the 1st *and* on every Monday.
 *
 * Returns null for an invalid expression, an unknown zone, or nothing within
 * four years -- which a real expression cannot be, but `0 0 30 2 *` is.
 */
export function nextRun(
  expression: string,
  timeZone: string,
  from: Date = new Date(),
): Date | null {
  const sets = parseCron(expression);
  if (!sets || !isValidTimezone(timeZone)) return null;

  // Both restricted means either matches; one restricted means that one decides.
  const domRestricted = sets.doms.length < 31;
  const dowRestricted = sets.dows.length < 7;

  let cursor = partsIn(from, timeZone);

  // Four years covers the worst legitimate case, 29 February on a cron that
  // also pins a weekday, with room to spare.
  for (let dayOffset = 0; dayOffset < 366 * 4; dayOffset += 1) {
    const dayStart = instantFrom({ ...cursor, hour: 0, minute: 0 }, timeZone);
    const onThisDay = partsIn(new Date(dayStart.getTime() + dayOffset * 86_400_000), timeZone);

    const monthOk = sets.months.includes(onThisDay.month);
    const domOk = sets.doms.includes(onThisDay.day);
    const dowOk = sets.dows.includes(onThisDay.weekday);
    const dayOk =
      monthOk && (domRestricted && dowRestricted ? domOk || dowOk : domOk && dowOk);

    if (dayOk) {
      for (const hour of sets.hours) {
        for (const minute of sets.minutes) {
          const candidate = instantFrom({ ...onThisDay, hour, minute }, timeZone);
          // Strictly future: a run happening this very minute is behind us.
          if (candidate.getTime() > from.getTime()) return candidate;
        }
      }
    }
  }

  return null;
}
